import { Country, Prisma } from '@commenda/prisma';
import { InvoicesList } from '@commenda/types/src/root-fi/types';
import { SWRFetcher } from '@commenda/utils';
import useSWR, { KeyedMutator } from 'swr';

// The types are slightly off after serializing and parsing this object, so
// this function takes the object returned from the API (whose types are slightly wrong)
// and deserialized is:
function deserializeInvoices(input: InvoicesList): InvoicesList | undefined {
  if (!input) {
    return undefined;
  }

  const result = input.map((invoice) => {
    return {
      ...invoice,
      tax_amount: invoice.tax_amount
        ? new Prisma.Decimal(invoice.tax_amount)
        : null,
      total_amount: invoice.total_amount
        ? new Prisma.Decimal(invoice.total_amount)
        : null,
      sub_total: invoice.sub_total
        ? new Prisma.Decimal(invoice.sub_total)
        : null,
      posted_date: new Date(invoice.posted_date),
      total_discount: invoice.total_discount
        ? new Prisma.Decimal(invoice.total_discount)
        : null,
    };
  });
  return result;
}

export function getInvoiceSearchQuery({
  companyId,
  corporationIds,
  countries,
  cursor,
  effectiveEndDate,
  effectiveStartDate,
  isInvalid,
  pageSize,
  states,
}: {
  companyId: number;
  corporationIds: number[];
  countries?: Country[];
  states?: string[];
  effectiveStartDate?: Date;
  effectiveEndDate?: Date;
  pageSize?: number;
  cursor?: string;
  isInvalid?: boolean;
}): string {
  const query = new URLSearchParams();
  query.append('companyId', companyId.toString());
  query.append('corporationIds', corporationIds.toString());

  if (countries && countries.length)
    query.append('countries', countries.toString());

  if (states && states.length) query.append('states', states.toString());

  if (effectiveStartDate)
    query.append('effectiveStartDate', effectiveStartDate?.toISOString());

  if (effectiveEndDate)
    query.append('effectiveEndDate', effectiveEndDate?.toISOString());

  query.append('pageSize', pageSize?.toString() || '100');
  if (cursor) query.append('cursor', cursor);

  if (isInvalid !== null && isInvalid !== undefined) {
    query.append('isInvalid', isInvalid ? 'true' : 'false');
  }

  return query.toString();
}

export default function useSearchInvoices(
  companyId: number,
  corporationIds: number[],
  token: string,
  options: {
    countries?: Country[];
    states?: string[];
    effectiveStartDate?: Date;
    effectiveEndDate?: Date;
    pageSize?: number;
    cursor?: string;
    isInvalid?: boolean;
  }
): {
  data: { invoices: InvoicesList | undefined; nextCursor: string | undefined };
  error: unknown;
  mutate: KeyedMutator<unknown>;
} {
  const {
    countries,
    states,
    effectiveStartDate,
    effectiveEndDate,
    pageSize,
    cursor,
    isInvalid,
  } = options;
  const query = getInvoiceSearchQuery({
    companyId,
    corporationIds,
    countries,
    cursor,
    effectiveEndDate,
    effectiveStartDate,
    isInvalid,
    pageSize,
    states,
  });

  const {
    data,
    error,
    mutate,
  }: {
    mutate: KeyedMutator<unknown>;
    error: unknown;
    data: { invoices: InvoicesList; nextCursor: string };
  } = useSWR(
    `integrations/data/accounting/invoices/search?${query}`,
    SWRFetcher({ method: 'GET' }, token)
  );

  return {
    data: {
      invoices: deserializeInvoices(data?.invoices),
      nextCursor: data?.nextCursor,
    },
    error,
    mutate,
  };
}
